import React, { useState, useRef, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Button,
  InputAdornment,
  useMediaQuery,
  CircularProgress,
  keyframes,
  FormControl,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { debounce } from "lodash";
import { searchResults } from "../../actions/searchActions";
import InfoDialog from "../../dialogs/InfoDialog";
import StopIcon from "@mui/icons-material/Stop";
import { showInsufficientCreditsModal } from "../../actions/creditActions";
import { deductCredits } from "../../utils/HelperFunction/deductCredits";
import useAudioProcessor from "../../utils/hooks/useAudioProcessor";
import AudioVisualizer from "./AudioVisualizer";
import ToggleButton from "./ToggleButton";
import SuggestionsDropdown from "./SuggestionsDropdown";
import { fetchListing } from "../../store/CaseSearch/actions";
import {
  BookOpen,
  Filter,
  Zap,
  MessageSquare,
  Info,
  Mic,
  X,
  Hash,
  FileText,
} from "react-feather";
import {
  fetchCaseSuggestionsApi,
  fetchLegalTermsSuggestionsApi,
} from "../../services/api/caseSearchApi";
import LogoLoader from "./LogoLoader";

const pulse = keyframes` 0% {
  box-shadow: 0 0 0 0 rgba(255, 64, 129, 0.4);
}
70% {
  box-shadow: 0 0 0 10px rgba(255, 64, 129, 0);
}
100% {
  box-shadow: 0 0 0 0 rgba(255, 64, 129, 0);
}`;

// Tabs
const tabs = [
  {
    label: "Judgements",
    icon: <FileText size={18} color="#0073A6" />,
    tooltip: "Search through case law judgments",
    color: "#0073A6",
    hoverColor: "#005A8B",
  },
  {
    label: "Maxims",
    icon: <Zap size={18} color="#F59E0B" />,
    tooltip: "Explore legal maxims and principles",
    color: "#F59E0B",
    hoverColor: "#D97706",
  },
  {
    label: "Terms",
    icon: <BookOpen size={18} color="#10B981" />,
    tooltip: "Browse legal terminology and definitions",
    color: "#10B981",
    hoverColor: "#059669",
  },
  {
    label: "Words & Phrases",
    icon: <MessageSquare size={18} color="#7C3AED" />,
    tooltip: "Search legal words and phrases",
    color: "#7C3AED",
    hoverColor: "#6D28D9",
  },
];

const CaseSearchHeader = ({
  activeTab,
  setActiveTab,
  toggleSidebar,
  resultsCount,
  searchMode,
  updateSearchMode,
  searchKeyword,
  setSearchKeyword,
  isSearchClicked,
  setIsSearchClicked,
  updateCurrentPage,
  setIsNewCaseLawSearch,
  setListingsCurrentPage,
  hasSearched,
  setHasSearched,
  openJudgement,
  setOpenJudgement,
  openJudgementText,
  setOpenJudgementText,
}) => {
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("xl"));

  // State management
  const [isListening, setIsListening] = useState(false);
  const [isAdvancedSearch, setIsAdvancedSearch] = useState(false);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [placeholder, setPlaceholder] = useState(
    "Search case law, judgments, or legal documents...",
  );

  const recognitionRef = useRef(null);
  const shouldListenRef = useRef(false);
  const inputRef = useRef(null);
  const paperRef = useRef(null);
  const itemRefs = useRef([]);
  const activeTabRef = useRef(activeTab);
  const audioData = useAudioProcessor(isListening);

  const dispatch = useDispatch();
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const credits = useSelector((state) => state.credit.credits);
  const userId = useSelector((state) => state.credit.userId);
  const latestQueryRef = useRef("");

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  // Voice recognition setup
  useEffect(() => {
    if (!window.webkitSpeechRecognition) {
      console.log("Speech recognition not supported");
      return;
    }

    const recognition = new window.webkitSpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      const interimTranscript = Array.from(event.results)
        .map((r) => r[0].transcript)
        .join("");
      setSearchKeyword(interimTranscript);
    };

    recognition.onerror = (event) => {
      console.error("Speech error:", event.error);
      stopRecognition();
    };

    recognition.onend = () => {
      if (shouldListenRef.current) {
        recognition.start();
      } else {
        setIsListening(false);
      }
    };

    recognitionRef.current = recognition;
  }, []);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (paperRef.current && !paperRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const startRecognition = () => {
    if (recognitionRef.current) {
      shouldListenRef.current = true;
      recognitionRef.current.start();
    }
  };

  const stopRecognition = () => {
    shouldListenRef.current = false;
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  };

  const cancelVoiceInput = () => {
    stopRecognition();
    setSearchKeyword("");
  };

  const acceptVoiceInput = () => {
    stopRecognition();
    handleSearch();
  };

  const handleClickInfoDialog = () => {
    setOpenInfoDialog(true);
  };

  const handleCloseInfoDialog = () => {
    setOpenInfoDialog(false);
  };

  // Search functionality
  const fetchResultsForActiveTab = async (keyword) => {
    if (activeTabRef.current === 0) {
      await dispatch(searchResults(keyword, "", 0, resultsCount, searchMode));
    } else {
      const listingType =
        activeTabRef.current === 1
          ? "maxims"
          : activeTabRef.current === 2
            ? "legalTerms"
            : "wordsAndPhrases";
      const category =
        activeTabRef.current === 1
          ? "maxim"
          : activeTabRef.current === 2
            ? "legal_term"
            : "word_phrase";
      await handleFetchListing(
        listingType,
        1,
        resultsCount,
        keyword,
        undefined,
        category,
      );
    }
  };

  const handleSearch = async (keyword = searchKeyword) => {
    if (!keyword.trim()) return;
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal());
      return;
    }

    setIsSearchClicked(true);
    setHasSearched(true);
    updateCurrentPage(1);
    setListingsCurrentPage(1);

    await fetchResultsForActiveTab(keyword);

    const deductionSuccess = deductCredits(
      { userId, creditsToDeduct: 1, event: "Search on Case Search Page" },
      dispatch,
    );

    if (!deductionSuccess) {
      console.warn(
        "Failed to deduct credits. Ensure retry or user notification.",
      );
    }
  };

  const handleSwitchChange = (event) => {
    setIsAdvancedSearch(event.target.checked);
    updateSearchMode(event.target.checked ? "vector" : "keyword");
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchKeyword(suggestion);
    setIsDropdownOpen(false);
    handleSearch(suggestion);
  };

  const closeCitationAndText = () => {
    setOpenJudgement({});
    setOpenJudgementText({});
  };

  const defaultMaxims = (listingType, letter, category) => {
    dispatch(fetchListing(listingType, letter, category));
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    const wasNonEmpty = searchKeyword.trim() !== "";
    setSearchKeyword(value);
    setIsDropdownOpen(value.length > 0);

    if (value.length > 2) {
      debouncedFetchSuggestions(value);
    } else {
      setSuggestions([]);
      setIsDropdownOpen(false);
    }

    if (value.trim() === "" && wasNonEmpty) {
      updateCurrentPage(1);
      setListingsCurrentPage(1);
      fetchResultsForActiveTab("");
    }
  };

  const debouncedFetchSuggestions = useCallback(
    debounce(async (query) => {
      if (query.length < 3) return;

      try {
        const activeTabNow =
          activeTabRef.current === 1
            ? "maxim"
            : activeTabRef.current === 2
              ? "legal_term"
              : "word_phrase";

        if (activeTabRef.current == 0) {
          if (!query.trim()) {
            setSuggestions([]);
            setIsDropdownOpen(false);
            return;
          }
          latestQueryRef.current = query;
          setLoadingSuggestions(true);
          try {
            const response = await fetchCaseSuggestionsApi(query);
            const responseData = response?.data;
            if (query === latestQueryRef.current) {
              setSuggestions(responseData?.data || []);
              setIsDropdownOpen(true);
            }
          } catch (error) {
            console.error("Error fetching suggestions:", error);
          } finally {
            setLoadingSuggestions(false);
          }
        } else {
          const payload = {
            filter: `type eq '${activeTabNow}'`,
            search: query,
          };
          setLoadingSuggestions(true);
          const response = await fetchLegalTermsSuggestionsApi(payload);
          const rawSuggestions = response?.data?.value || [];
          setSuggestions(
            rawSuggestions.slice(0, 5).map((item) => ({
              queryPlusText: item["@search.text"],
              docId: item.doc_id,
            })),
          );
          setIsDropdownOpen(true);
        }
      } catch (error) {
        console.error("Error fetching suggestions:", error);
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 300),
    [],
  );

  const handleFetchListing = (
    listingType,
    currentPage,
    resultsCount,
    searchTerm,
    letter,
    category,
  ) => {
    dispatch(fetchListing(listingType, currentPage, resultsCount, searchTerm, letter, category));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Escape") {
      setIsDropdownOpen(false);
      setFocusedIndex(-1);
    }
  };

  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
    activeTabRef.current = newValue;
    updateCurrentPage(1);
    setListingsCurrentPage(1);

    if (newValue === 0) {
      setPlaceholder("Search case law, judgments, or legal documents...");
    } else if (newValue === 1) {
      setPlaceholder("Search legal maxims and principles...");
    } else if (newValue === 2) {
      setPlaceholder("Search legal terms and definitions...");
    } else {
      setPlaceholder("Search legal words and phrases...");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        boxSizing: "border-box",
        overflow: "visible",
        backgroundColor: "#FFFFFF",
        borderRadius: "20px",
        padding: { xs: "16px", md: "20px 24px" },
        marginBottom: "3px",
        border: "1px solid #F1F5F9",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
      }}
    >
      {/* Row 1: Filter button + Search bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          width: "100%",
          minWidth: 0,
          mb: 1,
        }}
      >
        {/* Mobile Filter Button */}
        {isMobileView && (
          <IconButton
            size="medium"
            onClick={toggleSidebar}
            sx={{
              flexShrink: 0,
              color: "#0073A6",
              backgroundColor: "#F0F9FF",
              "&:hover": {
                backgroundColor: "#E0F2FE",
              },
            }}
          >
            <Filter size={20} />
          </IconButton>
        )}

        {/* Search Container */}
        <Box
          sx={{
            position: "relative",
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            backgroundColor: "#F8FAFC",
            borderRadius: "16px",
            border: "1px solid transparent",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "#F1F5F9",
            },
            "&:focus-within": {
              backgroundColor: "#FFFFFF",
              borderColor: "#0073A6",
              boxShadow: "0 0 0 3px rgba(0, 115, 166, 0.1)",
            },
          }}
        >
          {/* Search Icon */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              width: "48px",
              // height: "48px",
              color: "#0073A6",
            }}
          >
            <SearchIcon sx={{ fontSize: "20px" }} />
          </Box>

          {/* Input Field */}
          <TextField
            fullWidth
            placeholder={isListening ? "" : placeholder}
            value={isListening ? "" : searchKeyword}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            inputRef={inputRef}
            sx={{
              minWidth: 0,
              "& .MuiOutlinedInput-root": {
                border: "none",
                backgroundColor: "transparent",
                fontFamily:
                  "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                fontSize: "16px",
                "& fieldset": { border: "none" },
                "&:hover fieldset": { border: "none" },
                "&.Mui-focused fieldset": { border: "none" },
              },
              "& input": {
                padding: "12px 0px",
                color: "#111827",
                "&::placeholder": {
                  color: "#6B7280",
                  opacity: 1,
                },
              },
            }}
          />

          {/* Action Buttons — fixed width so loading never shifts layout */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "4px",
              padding: "0 12px",
              flexShrink: 0,
              width: activeTabRef.current === 0 ? "168px" : "60px",
              transition: "width 0.2s ease-in-out",
            }}
          >
            {loadingSuggestions ? (
              <LogoLoader size={isMobileView ? "sm" : "sm"} duration="1s" />
            ) : isListening ? (
              <>
                <Tooltip title="Cancel" placement="top">
                  <IconButton
                    size="small"
                    onClick={cancelVoiceInput}
                    sx={{
                      color: "#6B7280",
                      "&:hover": { backgroundColor: "#F3F4F6" },
                    }}
                  >
                    <X size={18} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Stop Recording" placement="top">
                  <IconButton
                    size="small"
                    onClick={acceptVoiceInput}
                    sx={{
                      backgroundColor: "#EF4444",
                      color: "#FFFFFF",
                      animation: `${pulse} 1.1s infinite`,
                      "&:hover": { backgroundColor: "#DC2626" },
                    }}
                  >
                    <StopIcon sx={{ fontSize: "16px" }} />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                {activeTabRef.current === 0 && (
                  <>
                    <Tooltip title="Keyword Search" placement="top">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setIsAdvancedSearch(false);
                          updateSearchMode("keyword");
                        }}
                        sx={{
                          color: isAdvancedSearch ? "#6B7280" : "#8B5CF6",
                          backgroundColor: isAdvancedSearch
                            ? "transparent"
                            : "rgba(139, 92, 246, 0.1)",
                          "&:hover": {
                            backgroundColor: isAdvancedSearch
                              ? "#F3F4F6"
                              : "rgba(139, 92, 246, 0.15)",
                          },
                        }}
                      >
                        <Hash size={18} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="AI Search" placement="top">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setIsAdvancedSearch(true);
                          updateSearchMode("vector");
                        }}
                        sx={{
                          color: isAdvancedSearch ? "#F59E0B" : "#6B7280",
                          backgroundColor: isAdvancedSearch
                            ? "rgba(245, 158, 11, 0.1)"
                            : "transparent",
                          "&:hover": {
                            backgroundColor: isAdvancedSearch
                              ? "rgba(245, 158, 11, 0.15)"
                              : "#F3F4F6",
                          },
                        }}
                      >
                        <Zap size={18} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Search Help" placement="top">
                      <IconButton
                        size="small"
                        onClick={handleClickInfoDialog}
                        sx={{
                          color: "#06B6D4",
                          "&:hover": { backgroundColor: "#ECFEFF" },
                        }}
                      >
                        <Info size={18} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                <Tooltip title="Voice Search" placement="top">
                  <IconButton
                    size="small"
                    onClick={startRecognition}
                    sx={{
                      color: "#EF4444",
                      "&:hover": { backgroundColor: "#FEF2F2" },
                    }}
                  >
                    <Mic size={18} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>

          {/* Suggestions Dropdown */}
          {isDropdownOpen && suggestions?.length > 0 && (
            <SuggestionsDropdown
              suggestions={suggestions}
              focusedIndex={focusedIndex}
              onSuggestionClick={handleSuggestionClick}
              itemRefs={itemRefs}
              ref={paperRef}
            />
          )}
        </Box>
      </Box>

      {/* Row 2: Tab Navigation — own row so it never overflows the search bar */}
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          width: "100%",
          flexWrap: "wrap",
          // marginTop: "12px",
        }}
      >
        {tabs.map((item, index) => (
          <Tooltip
            key={index}
            title={item.tooltip}
            arrow
            placement="bottom"
            enterDelay={400}
          >
            <Button
              variant="outlined"
              size="medium"
              className={activeTabRef.current === index ? "active" : ""}
              startIcon={item.icon}
              onClick={() => handleTabChange(index)}
              sx={{
                flex: 1,
                minWidth: 0,
                // height: "40px",
                padding: "8px 12px",
                fontSize: "13px",
                fontWeight: 500,
                textTransform: "none",
                borderRadius: "12px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                backgroundColor:
                  activeTabRef.current === index ? `${item.color}15` : "transparent",
                color: activeTabRef.current === index ? item.color : "#6B7280",
                border:
                  activeTabRef.current === index
                    ? `1px solid ${item.color}`
                    : "1px solid #E5E7EB",
                transition: "all 0.2s ease-in-out",
                "& .MuiButton-startIcon": { marginRight: "4px" },
                "& .MuiButton-startIcon svg": { flexShrink: 0 },
                "&:hover": {
                  backgroundColor:
                    activeTabRef.current === index ? `${item.color}25` : "#F9FAFB",
                  borderColor:
                    activeTabRef.current === index ? item.hoverColor : "#D1D5DB",
                },
              }}
            >
              {isMobileView ? null : item.label}
            </Button>
          </Tooltip>
        ))}
      </Box>

      {/* Audio Visualizer */}
      {isListening && (
        <Box sx={{ marginTop: "16px" }}>
          <AudioVisualizer
            isListening={isListening}
            audioLevel={audioData}
            component="case"
          />
        </Box>
      )}

      <InfoDialog open={openInfoDialog} handleClose={handleCloseInfoDialog} />
    </Box>
  );
};

export default CaseSearchHeader;
